import Color from "color";
import PropTypes from "prop-types";
import React, { Fragment } from "react";
import { useTheme } from "styled-components";
import {
  getAverageDOM,
  getAverageSoldToListPricePercentage
} from "../../utils/listings";
import { Dot, Line, ScatterPlot, SVG } from "./ScatterPlot";
import {
  Legend,
  LegendIconCircle,
  LegendIconDivider,
  Legends,
  Placeholder
} from "./styled/report-chart";

export default function TimeToSellChart({
  width,
  height,
  listings,
  hoverListingId,
  previewListingId,
  setPreviewListingId,
  hasLabel,
  horizontalLegends
}) {
  const theme = useTheme();
  const filteredListings = listings.filter(
    (listing) => listing.dom && listing.price
  );
  const hoverListing = filteredListings.find(
    (listing) => listing.recordId === hoverListingId
  );
  const previewListing = filteredListings.find(
    (listing) => listing.recordId === previewListingId
  );
  const soldListings = filteredListings.filter((listing) => listing.soldPrice);
  const averageSoldDOM = getAverageDOM(soldListings);
  const averageSoldToListPricePercentage = getAverageSoldToListPricePercentage(
    soldListings.filter((listing) => listing.originalListPrice)
  );
  const doms = filteredListings.map((listing) => listing.dom).filter(Boolean);
  const minDom = doms.length ? Math.min(...doms) : 0;
  const maxDom = doms.length ? Math.max(...doms) : 0;
  const minPrices = filteredListings
    .map((listing) =>
      listing.originalListPrice < listing.price
        ? listing.originalListPrice
        : listing.price
    )
    .filter(Boolean);
  const minPrice = minPrices.length ? Math.min(...minPrices) : 0;
  const maxPrices = filteredListings
    .map((listing) =>
      listing.originalListPrice > listing.price
        ? listing.originalListPrice
        : listing.price
    )
    .filter(Boolean);
  const maxPrice = maxPrices.length ? Math.max(...maxPrices) : 0;

  return (
    <>
      {!!listings.length && (
        <ScatterPlot
          width={width}
          height={height}
          minX={minDom}
          maxX={maxDom}
          minY={minPrice}
          maxY={maxPrice}
          xLabel="Days on market">
          {filteredListings.map((listing, index) => {
            const handleClick = (e) => {
              e.stopPropagation();
              setPreviewListingId(listing.recordId);
            };

            return (
              <Fragment key={listing.recordId}>
                {listing.originalListPrice && listing.price && (
                  <>
                    <Line
                      x1={listing.dom}
                      y1={listing.originalListPrice}
                      x2={listing.dom}
                      y2={listing.price}
                      color={listing.statusColor}
                      width={2}
                    />
                    <Dot
                      x={listing.dom}
                      y={listing.originalListPrice}
                      outlineColor={listing.statusColor}
                      color={theme.styleGuide.colors.white}
                      onClick={handleClick}
                    />
                  </>
                )}
                <Dot
                  x={listing.dom}
                  y={listing.price}
                  outlineColor={listing.statusColor}
                  color={Color(listing.statusColor).lighten(0.2).toString()}
                  onClick={handleClick}
                  hasLabel={hasLabel}
                  label={index + 1}
                />
              </Fragment>
            );
          })}
          {(!!previewListing || !!hoverListing) && (
            <>
              <Line
                x1={(previewListing || hoverListing).dom}
                y1={(previewListing || hoverListing).originalListPrice}
                x2={(previewListing || hoverListing).dom}
                y2={(previewListing || hoverListing).price}
                color={(previewListing || hoverListing).statusColor}
                width={2}
              />
              <Dot
                x={(previewListing || hoverListing).dom}
                y={(previewListing || hoverListing).originalListPrice}
                radius={12}
                outlineColor={(previewListing || hoverListing).statusColor}
                color={theme.styleGuide.colors.white}
              />
              <Dot
                x={(previewListing || hoverListing).dom}
                y={(previewListing || hoverListing).price}
                radius={12}
                outlineColor={(previewListing || hoverListing).statusColor}
                color={Color((previewListing || hoverListing).statusColor)
                  .lighten(0.2)
                  .toString()}
              />
              {!!previewListing && (
                <SVG
                  width={12}
                  height={12}
                  x={previewListing.dom}
                  y={previewListing.price}
                  color={theme.styleGuide.colors.white}
                  transform="translate(-6, -5)"
                  paths={[
                    "M12.246.188a.75.75 0 01.066 1.058l-7.5 8.5a.75.75 0 01-1.092.034l-3.5-3.5a.75.75 0 111.06-1.06l2.936 2.935L11.188.254a.75.75 0 011.058-.066z"
                  ]}
                />
              )}
            </>
          )}
        </ScatterPlot>
      )}
      {!listings.length && <Placeholder />}
      <Legends
        hasData={!!averageSoldDOM || !!averageSoldToListPricePercentage}
        horizontalLegends={horizontalLegends}>
        <Legend horizontalLegend={!horizontalLegends}>
          <LegendIconCircle />
          Original list price
        </Legend>
        {!horizontalLegends && <LegendIconDivider />}
        <Legend>
          <LegendIconCircle isFilled />
          Most recent price or sold price
        </Legend>
      </Legends>
    </>
  );
}

TimeToSellChart.defaultProps = {
  setPreviewListingId: () => {}
};

TimeToSellChart.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  hoverListingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  previewListingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setPreviewListingId: PropTypes.func
};
